import React from 'react';
import { useHistory } from 'react-router-dom';
import URI from 'urijs';

export function useLocationQueryState(queryParamName: string, defaultValue?: string) {
	const { location, listen } = useHistory();
	const searchParams = new URLSearchParams(location.search);
	const value = searchParams.get(queryParamName) ?? defaultValue;
	const [queryParamValue, setQueryParamValue] = React.useState(value);
	const setValue = React.useCallback(
		(tabKey: string | undefined) => {
			const nextSearchValue = new URLSearchParams(location.search);
			nextSearchValue.delete(queryParamName);
			if (tabKey) {
				nextSearchValue.set(queryParamName, tabKey);
			}
			setQueryParamValue(tabKey);
			const uri = new URI(window.location.href).query(nextSearchValue.toString());
			window.history.replaceState(window.history.state, '', uri.valueOf());
		},
		[location, queryParamName]
	);

	React.useEffect(() => {
		const unlisten = listen(currentLocation => {
			const currentSearchValues = new URLSearchParams(currentLocation.search);
			if (currentSearchValues.has(queryParamName)) {
				setQueryParamValue(currentSearchValues.get(queryParamName)?.toString());
			}
		});

		return () => {
			unlisten();
			// Browser location is accurate, where-as the `useHistory().location` is an out-of-date reference during unmount.
			const nextSearchValue = new URLSearchParams(window.location.search);
			nextSearchValue.delete(queryParamName);
			const uri = new URI(window.location.href).query(nextSearchValue.toString());
			window.history.replaceState(window.history.state, '', uri.valueOf());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return [queryParamValue, setValue] as const;
}
